import './Product.css';
import {useMediaQuery} from "react-responsive";
import {Link, useNavigate} from "react-router-dom";
import {addToCart} from "../../../../CartHandler.js";
import React, {useMemo, useState} from "react";
import {useNotification} from "../../../../context/NotificationContext/NotificationContext.jsx";
import {useBasket} from "../../../../context/BasketContext/BasketContext.jsx";
import {checkStock} from "../../../../ProductsHandler.js";

const Product = React.forwardRef(({product, display}, ref) => {
    const isMobile = useMediaQuery({maxWidth: 768});
    const navigate = useNavigate();
    const {setDisplayNotification} = useNotification();
    const {bumpBasketVersion} = useBasket();
    const [adding, setAdding] = useState(false);

    const defaultSellableVariant = useMemo(() => {
        if (!product?.variants?.length) return null;
        return product.variants.find(v => v.availableForSale && !v.currentlyNotInStock) || null;
    }, [product?.variants]);

    const productPath = product.handle
        ? `/produkt/${product.handle}`
        : `/produkt?id=${encodeURIComponent(product.id)}`;

    const addItemToCart = async () => {
        try {
            if (adding) return;
            setAdding(true);

            const chosen = defaultSellableVariant ?? product.variants?.[0];
            if (!chosen) {
                setDisplayNotification({type: 'error', message: 'Brak dostępnych wariantów tego produktu.'});
                return;
            }

            const stock = await checkStock(chosen.id);
            if (stock === 0 || chosen.currentlyNotInStock === true || chosen.availableForSale === false) {
                setDisplayNotification({
                    type: 'error',
                    message: 'Wybrany wariant jest niedostępny. Wybierz inny na stronie produktu.'
                });
                navigate(productPath);
                return;
            }

            const res = await addToCart(chosen.id, 1);
            if (res?.__error || res?.addedQuantity === 0) {
                setDisplayNotification({type: 'error', message: 'Nie udało się dodać do koszyka.'});
                return;
            }

            setDisplayNotification({type: 'message', message: 'Produkt dodano do koszyka!'});
            bumpBasketVersion();
        } catch (e) {
            console.error(e);
            setDisplayNotification({type: 'error', message: 'Nie udało się dodać do koszyka. Spróbuj ponownie.'});
        } finally {
            setAdding(false);
        }
    };

    const hasDiscount = product.price.discountedPrice !== null && product.price.discountedPrice !== undefined;
    const mainPrice = (hasDiscount ? product.price.discountedPrice : product.price.price).toFixed(2);
    const wasPrice = hasDiscount ? product.price.price.toFixed(2) : null;

    const hasSellableVariant = Boolean(defaultSellableVariant);
    const unavailableClass = !hasSellableVariant ? `product-add-to-cart-quick-unavailable--${display}` : '';
    return (
        <Link
            ref={ref}
            to={productPath}
            className={`product-container--${display}`}
            draggable={false}
        >
            {import.meta.env.VITE_IS_CHRISTMAS===true && <div className={`product-hat-container--${display}`}>
                <img src='/images/santa-hat-icon.png' alt='santa hat' className={`product-hat-image--${display}`} />
            </div>}
            <div className={`product-image--${display}`}>
                <img loading="lazy" src={product.imageUrl} alt={product.alt || product.name || 'Produkt'}
                     draggable={false}/>
            </div>

            {display === 'grid' ? (
                <h3 className={`product-name--${display}`}>{product.name}</h3>
            ) : (
                <div className="product-list-wrapper">
                    <div className={`product-name--${display}`}>{product.name}</div>
                </div>
            )}

            {/* PRICE — MOBILE (list) */}
            {isMobile && (
                <div className={`product-price--${display}`}>
                    {hasDiscount && <span className="was-price was-price--list">{mainPrice} zł</span>}
                    {hasDiscount && <span className="now-price">{wasPrice} zł</span>}
                    {!hasDiscount && <span className="now-price-no-discount">{mainPrice} zł</span>}
                </div>
            )}

            {/* “last 30 days” label — MOBILE LIST ONLY (separate grid row) */}
            {isMobile && display === 'list' && hasDiscount && (
                <div id="last-moth-price--list">
                    Cena sprzed ostatnich 30 dni: {mainPrice} zł
                </div>
            )}

            {/* Quick info about default variant — LIST only */}
            {product.variants.length > 1 && display === 'list' && (
                <div className={`product-add-to-cart-quick-snippet--${display}`}>
                    {hasSellableVariant ? (
                        <>
                            <span>Domyślny wariant:</span>
                            {defaultSellableVariant.options.map((o, i) => {
                                if(o.name==="Kolor"){
                                    return <span className='variant-container--list' key={i}>{o.name}: <div className='product-variant-color' style={{backgroundColor:o.value}}></div></span>
                                }
                                return <span key={i}>{o.name}: {o.value}</span>
                            })}
                        </>
                    ) : (
                        <span>Brak wariantów dostępnych od ręki</span>
                    )}
                </div>
            )}

            {/* PRICE — DESKTOP/TABLET */}
            {!isMobile && (
                <div className={`product-price--${display}`}>
                    {/* Inline label ABOVE prices on desktop list */}
                    {display === 'list' && hasDiscount && (
                        <div className="last-moth-price-inline">
                            Cena sprzed ostatnich 30 dni: {mainPrice} zł
                        </div>
                    )}


                    {hasDiscount && <span className="was-price was-price--list">{mainPrice} zł</span>}
                    {hasDiscount && <span className="now-price">{wasPrice} zł</span>}
                    {!hasDiscount && <span className="now-price-no-discount">{mainPrice} zł</span>}
                </div>
            )}

            {/* Quick add — LIST */}
            {display === 'list' && (
                <div
                    className={`product-add-to-cart-quick--${display} ${unavailableClass} ${!hasSellableVariant ? 'is-disabled' : ''}`}
                    aria-disabled={!hasSellableVariant || adding}
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!hasSellableVariant || adding) return;
                        addItemToCart();
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!hasSellableVariant || adding) return;
                            addItemToCart();
                        }
                    }}
                    title={!hasSellableVariant ? 'Wybierz wariant na stronie produktu' : 'Dodaj do koszyka'}
                />
            )}

            {/* GRID extras */}
            {display === 'grid' && hasDiscount && (
                <span id='last-month-price--grid'>
          Cena sprzed ostatnich 30 dni: {mainPrice} zł
          <div
              className={`product-add-to-cart-quick--${display} ${unavailableClass} ${!hasSellableVariant ? 'is-disabled' : ''}`}
              aria-disabled={!hasSellableVariant || adding}
              role="button"
              tabIndex={0}
              onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!hasSellableVariant || adding) return;
                  addItemToCart();
              }}
              onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!hasSellableVariant || adding) return;
                      addItemToCart();
                  }
              }}
              title={!hasSellableVariant ? 'Wybierz wariant na stronie produktu' : 'Dodaj do koszyka'}
          >
            {product.variants.length > 1 && hasSellableVariant && (
                <div className={`product-add-to-cart-quick-snippet--${display}`}>
                    <span>Domyślny wariant:</span>
                    {defaultSellableVariant.options.map((o, i) => (
                        <span key={i}>{o.name}: {o.value}</span>
                    ))}
                </div>
            )}
          </div>
        </span>
            )}

            {display === 'grid' && !hasDiscount && (
                <div className='no-discount-add-quick-container'>
                    <div
                        className={`product-add-to-cart-quick--${display} ${unavailableClass} ${!hasSellableVariant ? 'is-disabled' : ''}`}
                        aria-disabled={!hasSellableVariant || adding}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!hasSellableVariant || adding) return;
                            addItemToCart();
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                if (!hasSellableVariant || adding) return;
                                addItemToCart();
                            }
                        }}
                        title={!hasSellableVariant ? 'Wybierz wariant na stronie produktu' : 'Dodaj do koszyka'}
                    />
                </div>
            )}
        </Link>
    );
});

export {Product};
export default Product;
